import { useTheme } from '@emotion/react'
import { AppState, ConfigurationContext } from '@news-mono/web-common'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import url from 'url'
import { Skeleton } from '../../../src/content/Skeleton/Skeleton'
import { mapVisitorType } from '../../__App'
import { themedValue } from '../../__styling'
import { MastheadPreference } from '../components/MastheadPreference'
import { EmailPreferenceItem } from '../components/PreferenceSection'
import {
    StyledPerthNowLogo,
    StyledTheWestLogo,
} from '../components/PreferenceSection.styled'
import { getMastheadPreferences } from '../config'
import { PreferenceCardContext } from '../contexts/PreferenceCardContext'
import { EPC_STORAGE_KEY, ProfileStorage } from '../EmailPreference'
import {
    StyledBody,
    StyledEmail,
    StyledEmailPreference,
    StyledFooter,
    StyledGreeting,
    StyledHeading,
    StyledLink,
    StyledWebLink,
} from '../EmailPreference.styled'
import {
    useEmailPreferenceData,
    useGetEmailToken,
    useGetPreferences,
} from '../hooks/useEmailPreferenceData'
import { getStorage, useResolveEmail } from '../hooks/useUnsubscribe'

export const EmailPreferenceLoggedIn = () => {
    const { emailServiceApi } = useContext(ConfigurationContext)
    const storageData = getStorage(EPC_STORAGE_KEY) as ProfileStorage | null
    const theme = useTheme()
    const { userName, userEmail, isLoggedIn, subscriptionType } = useSelector(
        (state: AppState) => state.authentication,
    )
    const tokenRef = useRef<{
        urlToken?: string
        profileToken?: string
    }>()
    const [useProfileEmail, setUseProfileEmail] = useState<boolean>(false)

    const visitorType = mapVisitorType(isLoggedIn, subscriptionType)

    const [userFirstName] = userName.split(' ')

    const {
        isLoading: isTokenLoading,
        isError: isTokenError,
        data: tokenData,
        error: tokenError,
    } = useGetEmailToken({ enabled: isLoggedIn })

    if (tokenData?.token) {
        tokenRef.current = {
            ...tokenRef.current,
            profileToken: tokenData?.token,
        }
    }

    const {
        isLoading: isPreferenceLoading,
        isError: isPreferenceError,
        data: preferenceData,
        error: preferenceError,
    } = useEmailPreferenceData(
        useProfileEmail
            ? tokenRef.current?.profileToken
            : tokenRef.current?.urlToken,
    )

    if (isTokenError) {
        throw new Error(tokenError as string)
    }

    if (isPreferenceError) {
        throw new Error(preferenceError as string)
    }

    const mastheadPreferences = useGetPreferences(
        preferenceData?.result,
        getMastheadPreferences(theme),
    )

    useEffect(() => {
        const { token } = url.parse(window.location.href, true).query
        if (token) {
            if (typeof token !== 'string') {
                throw new Error('Invalid link.')
            }
            tokenRef.current = {
                ...tokenRef.current,
                urlToken: token,
            }
        }

        const useProfile =
            !token || (token && storageData && storageData.useProfileEmail)
        setUseProfileEmail(!!useProfile)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    const { resolveEmailError, data: emailsData } = useResolveEmail(
        useProfileEmail,
        emailServiceApi,
        tokenRef.current?.urlToken,
        userEmail,
    )
    const meta = themedValue(theme, {
        thewest: {
            logoGroup: [<StyledTheWestLogo />, <StyledPerthNowLogo />],
            contactLink: '/contact',
        },
        perthnow: {
            logoGroup: [<StyledPerthNowLogo />, <StyledTheWestLogo />],
            contactLink: '/contact-us',
        },
        sevennews: {
            logoGroup: [],
            contactLink: '/contact-us',
        },
        fallback: {
            logoGroup: [],
            contactLink: '/contact-us',
        },
    })

    const ToggleHandler = async (
        checked: boolean,
        item: EmailPreferenceItem,
    ): Promise<void> => {
        const api = `${emailServiceApi}/email-preference-center/toggle-preference`
        const response = await fetch(api, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: useProfileEmail
                    ? tokenRef.current?.profileToken || ''
                    : tokenRef.current?.urlToken || '',
            },
            body: JSON.stringify({
                account: item.subAccount,
                listId: item.guid,
                toggleState: item.optIn ? 'off' : 'on',
                unsubscribeGroupId: item.unSubId,
            }),
        })

        if (!response.ok) {
            throw new Error('Network response was not ok')
        }
    }
    const currentToken = tokenRef.current?.urlToken || null
    const unSubscribeAllLink = currentToken
        ? `/manage-email-preferences/unsubscribe/all?token=${currentToken}`
        : `/manage-email-preferences/unsubscribe/all`

    function onSwitchEmailClick(e: React.MouseEvent<HTMLElement>) {
        e.preventDefault()
        setUseProfileEmail((useProfileEmail) => !useProfileEmail)
        sessionStorage.setItem(
            EPC_STORAGE_KEY,
            JSON.stringify({
                useProfileEmail: !useProfileEmail,
            }),
        )
    }

    return (
        <StyledEmailPreference>
            <StyledHeading>Email Preferences</StyledHeading>
            <StyledGreeting>
                Hi, {userFirstName}!&nbsp; You are updating preferences
                for&nbsp;
                {(
                    <strong>
                        {useProfileEmail
                            ? emailsData.profileEmail
                            : emailsData.tokenEmail}
                    </strong>
                ) || <Skeleton height={25} width={170} />}
            </StyledGreeting>
            {currentToken && emailsData.profileEmail !== emailsData.tokenEmail && (
                <StyledEmail>
                    Switch to update for{' '}
                    {
                        <strong>
                            {!useProfileEmail
                                ? emailsData.profileEmail
                                : emailsData.tokenEmail}
                        </strong>
                    }
                    ?&nbsp;
                    <StyledLink onClick={(e) => onSwitchEmailClick(e)}>
                        Click here
                    </StyledLink>
                </StyledEmail>
            )}
            <StyledBody>
                Pick and choose the news that you want delivered to your inbox.
            </StyledBody>
            {mastheadPreferences.map((preferences, index) => (
                <PreferenceCardContext.Provider
                    value={{
                        toggleHandler: ToggleHandler,
                    }}
                    key={index}
                >
                    <MastheadPreference
                        preferences={preferences}
                        logo={meta.logoGroup[index]}
                        visitorType={visitorType}
                        isLoading={isTokenLoading || isPreferenceLoading}
                    />
                </PreferenceCardContext.Provider>
            ))}
            <StyledFooter>
                <h2>Looking for something else?</h2>
                <StyledWebLink to={meta.contactLink}>Contact us</StyledWebLink>
                <StyledWebLink to={unSubscribeAllLink}>
                    Unsubscribe from all promos and marketing
                </StyledWebLink>
            </StyledFooter>
        </StyledEmailPreference>
    )
}
